<script setup lang="ts">
import type { MenuItem } from 'primevue/menuitem';
import { useI18n } from 'vue-i18n';
import { logoutUser } from '../../dataProviders/auth';
import { useUserStore } from '../../stores/user';
import { defaultPerPage } from '../../constants/Pagination';

const { t } = useI18n();
const router = useRouter();
const user = useUserStore();

async function command() {
  await logoutUser();
  user.isAuthenticated = false;
  router.push({ name: 'login' });
};

// type NavigationItem = MenuItem & { component?: 'NavRouterLink' | '' }

const profileBtn: MenuItem = {
  label: user.profile.info.name,
  role: user.profile.access_level,
  icon: 'pi pi-profile',
  component: 'NavProfileDropdown',
  items: [{
    label: t('nav.profile'),
    icon: 'pi pi-user',
    route: '/profile',
    component: 'NavRouterLink'
  }, {
    label: 'Мащаб',
    icon: 'pi pi-sliders-h',
    component: 'ScaleSelector'
  }, {
    label: t('nav.logout'),
    icon: 'pi pi-sign-out',
    command,
  }],
};
const userLinks: MenuItem[] = [
  {
    label: t('nav.myOrders'),
    icon: 'pi pi-cart-plus',
    route: '/orders',
    component: 'NavRouterLink'
  },
  {
    label: t('nav.invoices'),
    icon: 'pi pi-file-pdf',
    route: '/invoices',
    component: 'NavRouterLink'
  },
  {
    label: t('nav.materials'),
    icon: 'pi pi-info-circle',
    route: '/materials',
    component: 'NavRouterLink'
  },
];

const lockedLinks: MenuItem[] = [
  {
    label: t('nav.allOrders'),
    icon: 'pi pi-box',
    route: '/admin/orders',
    component: 'NavRouterLink'
  },
  {
    label: t('nav.activeOrders'),
    icon: 'pi pi-file-edit',
    route: '/admin/active-orders',
    component: 'NavRouterLink'
  },
  {
    label: t('nav.settings'),
    icon: 'pi pi-cog',
    items: [{
      label: t('nav.materials'),
      icon: 'pi pi-info-circle',
      route: '/admin/materials',
      component: 'NavRouterLink'
    }, {
      label: t('nav.colors'),
      icon: 'pi pi-palette',
      route: '/admin/colors',
      component: 'NavRouterLink'
    }],
  },

];

const adminLinks: MenuItem[] = [
  {
    label: t('nav.invoices'),
    icon: 'pi pi-file-pdf',
    route: '/admin/invoices',
    component: 'NavRouterLink'
  },
  {
    label: t('nav.files'),
    icon: 'pi pi-images',
    route: '/admin/files',
    component: 'NavRouterLink'
  },
  {
    label: t('nav.users'),
    icon: 'pi pi-users',
    route: '/admin/users',
    component: 'NavRouterLink'
  },
];

function getAdminLinks() {
  const links = [...lockedLinks, ...adminLinks, profileBtn].filter(Boolean);
  const settings = links.find(link => link.label === t('nav.settings'));
  if (settings) {
    settings.items?.push({
      label: t('nav.prices'),
      icon: 'pi pi-calculator',
      route: '/admin/prices',
      component: 'NavRouterLink'
    });

    settings.items?.push({
      label: t('nav.news'),
      icon: 'pi pi-inbox',
      route: '/admin/news',
      component: 'NavRouterLink'
    });
  }

  return links;
}

const navigation = computed<MenuItem[]>(() => {
  switch (user.profile.access_level) {
    case 'Moderator':
      return [...lockedLinks, profileBtn];
    case 'Admin':
      return getAdminLinks();
    default:
      return [...userLinks, profileBtn];
  }
});
</script>

<template>
  <header class="sticky left-0 top-0 z-[100]">
    <Menubar :breakpoint="user.profile.access_level === 'User' ? '820px' : '1260px'" :model="navigation"
      :pt="{ root: 'px-4 shadow-b-sm rounded-0 border-2 border-b-solid border-b-primary bg-transparent', menu: 'ml-auto', end: 'm-0', button: 'ml-auto w-9 h-9', menubuttonicon: 'w-6 h-6' }">

      <template #start>
        <router-link :to="{ name: 'home', query: { page: 1, per_page: defaultPerPage } }">
          <img class="h-15" src="/powerprint_logo.png" alt="">
        </router-link>
      </template>

      <template #item="{ item, props, hasSubmenu }">
        <NavScaleSelector v-if="item.component === 'ScaleSelector'" :item="item" :router-action="props.action" />

        <NavRouterLink v-else-if="item.component === 'NavRouterLink'" :item="item" :router-action="props.action" />

        <NavProfileDropdown v-else-if="item.component === 'NavProfileDropdown'" :item="item"
          :router-action="props.action" :profile="user.profile" />

        <NavButton v-else :item="item" :router-action="props.action" :has-submenu="!!hasSubmenu" />
      </template>
    </Menubar>

    <NewsBanner v-if="user.isAuthenticated" />

  </header>
</template>